/**
 * Tiny IN-MEMORY helper that remembers the last rescan-from height
 * the user picked for a given Monero address inside the current tab.
 * Lets the MoneroRestorePanel keep its "current scan-from year"
 * stable while the user clicks "Search 1 year earlier" repeatedly.
 *
 * Address-keyed (so juggling multiple wallets doesn't collide).
 * 30-day TTL — beyond that, the cached "first used" date is too
 * stale to be useful and we'd rather start fresh than mislead.
 *
 * Privacy note (May 2026 audit):
 * Earlier builds persisted this to `localStorage`
 * (`winbit32:monero:last-rescan-from:<address>`). That meant every
 * Monero address the user ever rescanned was tied to a wallet
 * fingerprint on disk. The cache is now in-memory only; legacy
 * entries are wiped by {@link sweepLegacyMoneroRescanCache} on boot.
 */

export interface MoneroRescanCacheEntry {
	/** Block height the user last submitted to `/import_wallet_request`. */
	height: number;
	/** Approximate "X years ago" the height corresponds to. */
	yearsAgo: number;
	/** Epoch ms at which the entry was written. */
	savedAt: number;
	/**
	 * Block height we know LWS has already scanned to before this
	 * rescan was submitted. Used by the scan progress bar so that
	 * when the user clicks "Search 1 year earlier" and LWS rewinds
	 * its `scan_height` to the new (lower) `height`, the bar can
	 * keep the previously-scanned tail painted as **scanned** —
	 * conveying "only the extra year is genuinely new ground" —
	 * instead of repainting the entire chain as pending. Optional
	 * because legacy entries written before this field existed
	 * just won't have it; readers should treat `undefined` as "no
	 * prior cached tail".
	 */
	previousScannedHeight?: number;
}

const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const LEGACY_KEY_PREFIX = 'winbit32:monero:last-rescan-from:';

const _rescanCache = new Map<string, MoneroRescanCacheEntry>();

let _legacySweepDone = false;
export function sweepLegacyMoneroRescanCache(): void {
	if (_legacySweepDone) return;
	_legacySweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		const doomed: string[] = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (key && key.startsWith(LEGACY_KEY_PREFIX)) doomed.push(key);
		}
		for (const key of doomed) {
			try { localStorage.removeItem(key); } catch { /* */ }
		}
		if (doomed.length > 0) {
			console.info('[xmr-rescan-cache] swept %d legacy entries',
				doomed.length);
		}
	} catch (err) {
		console.warn('[xmr-rescan-cache] legacy sweep failed:',
			(err as Error).message);
	}
}

/** Test-only: wipe the in-memory cache between specs. */
export function __resetMoneroRescanCacheForTests(): void {
	_rescanCache.clear();
	_legacySweepDone = false;
}

export function readRescanCache(address: string | null | undefined): MoneroRescanCacheEntry | null {
	if (!address) return null;
	const parsed = _rescanCache.get(address);
	if (!parsed) return null;
	if (typeof parsed.height !== 'number'
		|| typeof parsed.yearsAgo !== 'number'
		|| typeof parsed.savedAt !== 'number') {
		return null;
	}
	if (Date.now() - parsed.savedAt > TTL_MS) {
		_rescanCache.delete(address);
		return null;
	}
	// Drop a malformed `previousScannedHeight` rather than passing it
	// through to the bar logic, which assumes number or `undefined`.
	const cleaned: MoneroRescanCacheEntry = {
		height: parsed.height,
		yearsAgo: parsed.yearsAgo,
		savedAt: parsed.savedAt,
		previousScannedHeight: typeof parsed.previousScannedHeight === 'number'
			? parsed.previousScannedHeight
			: undefined,
	};
	return cleaned;
}

export function writeRescanCache(
	address: string | null | undefined,
	entry: Omit<MoneroRescanCacheEntry, 'savedAt'>,
): void {
	if (!address) return;
	_rescanCache.set(address, {
		...entry,
		savedAt: Date.now(),
	});
}

export function clearRescanCache(address: string | null | undefined): void {
	if (!address) return;
	_rescanCache.delete(address);
}
